export type QueueAccount = {
  userId: string;
  domain: string;
  organizationId?: string;
};
export type RetryReceipt = {
  success: true;
  request_id: string;
  job_id: string;
  revision: number;
  action: "retry";
};
export type RetryCommand = {
  jobId: string;
  revision: number;
  requestId: string;
  body: string;
};
export type RetryState = {
  pending: RetryCommand | null;
  busy: boolean;
  error: string;
  success: RetryReceipt | null;
};
export class QueueHttpError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}
export const queueAccountKey = (account: QueueAccount) =>
  JSON.stringify([
    account.userId,
    account.domain,
    account.organizationId || "",
  ]);
export async function queueRequest<T>(
  base: string,
  account: QueueAccount,
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set("Content-Type", "application/json");
  headers.set("X-Shop-Domain", account.domain);
  if (account.organizationId)
    headers.set("X-Organization-Id", account.organizationId);
  const response = await fetch(`${base}/api/warehouse/sync/jobs${path}`, {
    ...options,
    headers,
    credentials: "include",
    cache: "no-store",
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || !data || data.success !== true)
    throw new QueueHttpError(
      data?.error || "The server reply could not be confirmed",
      response.ok ? 500 : response.status,
    );
  return data as T;
}
type Storage = Pick<
  globalThis.Storage,
  "getItem" | "setItem" | "removeItem" | "length" | "key"
>;
export function createShopifyRetry(
  base: string,
  account: QueueAccount,
  storage: Storage,
) {
  const prefix = "whenly-shopify-retry-v1:" + queueAccountKey(account) + ":";
  let state: RetryState = {
    pending: null,
    busy: false,
    error: "",
    success: null,
  };
  const restore = () => {
    const keys = Array.from({ length: storage.length }, (_, index) =>
      storage.key(index),
    )
      .filter((key): key is string => !!key && key.startsWith(prefix))
      .sort();
    for (const key of keys) {
      const saved = storage.getItem(key);
      if (!saved) continue;
      const value = JSON.parse(saved) as RetryCommand,
        body = JSON.parse(value.body);
      if (
        !value.jobId ||
        !Number.isSafeInteger(value.revision) ||
        value.revision < 1 ||
        typeof value.requestId !== "string" ||
        key !== prefix + value.requestId ||
        body.request_id !== value.requestId ||
        body.revision !== value.revision ||
        body.expected_user_id !== account.userId
      )
        throw new Error("Invalid saved retry");
      return value;
    }
    return null;
  };
  try {
    state.pending = restore();
  } catch {
    state.error =
      "Saved retry data could not be read. Browser storage must be available before sending a retry.";
  }
  const listeners = new Set<() => void>();
  const update = (patch: Partial<RetryState>) => {
    state = { ...state, ...patch };
    listeners.forEach((fn) => fn());
  };
  const retry = async () => {
    if (!state.pending || state.busy) return;
    const pending = state.pending;
    update({ busy: true, error: "", success: null });
    try {
      const result = await queueRequest<RetryReceipt>(
        base,
        account,
        `/${encodeURIComponent(pending.jobId)}/retry`,
        { method: "POST", body: pending.body },
      );
      if (
        result.request_id !== pending.requestId ||
        result.job_id !== pending.jobId ||
        result.revision !== pending.revision + 1 ||
        result.action !== "retry"
      )
        throw new QueueHttpError(
          "The retry receipt could not be confirmed. Retry the saved request.",
          500,
        );
      storage.removeItem(prefix + pending.requestId);
      update({ pending: restore(), success: result });
    } catch (error) {
      const rejected =
        error instanceof QueueHttpError &&
        [400, 409, 422].includes(error.status);
      if (rejected) {
        try {
          storage.removeItem(prefix + pending.requestId);
          update({ pending: restore() });
        } catch {
          /* Keep the original request if storage fails. */
        }
      }
      update({
        error:
          error instanceof Error
            ? error.message
            : "The outcome is unknown. Retry the saved request.",
      });
    } finally {
      update({ busy: false });
    }
  };
  return {
    snapshot: () => state,
    subscribe: (fn: () => void) => {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    retry,
    submit: async (jobId: string, revision: number) => {
      if (state.pending || state.busy) return;
      const requestId = crypto.randomUUID();
      const command = {
        jobId,
        revision,
        requestId,
        body: JSON.stringify({
          request_id: requestId,
          revision,
          expected_user_id: account.userId,
        }),
      };
      try {
        const previous = restore();
        if (previous) {
          update({
            pending: previous,
            error: "A saved request must be confirmed first.",
          });
          return;
        }
        storage.setItem(prefix + requestId, JSON.stringify(command));
      } catch {
        update({
          error:
            "The browser could not save this retry. Allow browser storage before trying again.",
        });
        return;
      }
      update({ pending: command, success: null, error: "" });
      await retry();
    },
  };
}
